import { format, parseISO } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, MapPinned } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

import {
  getSafetyIssueCreatorName,
  type SafetyIssuesSortKey,
  type SafetyIssuesSortState,
} from './listState';
import type { SafetyIssue } from './types';

interface SafetyIssuesTableProps {
  issues: SafetyIssue[];
  layoutNames: Map<number, string>;
  onSortChange: (key: SafetyIssuesSortKey) => void;
  safetyPath: string;
  sort: readonly SafetyIssuesSortState[];
}

const layoutLink = (issue: SafetyIssue, safetyPath: string) => {
  if (issue.layout == null || issue.position_x == null || issue.position_y == null) return null;
  const params = new URLSearchParams({
    layout: String(issue.layout),
    x: String(issue.position_x),
    y: String(issue.position_y),
  });
  return `${safetyPath}?${params}`;
};

const IssueStatus = ({ issue }: { issue: SafetyIssue }) => (
  <Badge variant={issue.status === 'OPEN' ? 'destructive' : 'success'}>
    {issue.status === 'OPEN' ? 'Open' : 'Resolved'}
  </Badge>
);

export const SafetyIssuesTable = ({
  issues,
  layoutNames,
  onSortChange,
  safetyPath,
  sort,
}: SafetyIssuesTableProps) => {
  const navigate = useNavigate();

  if (issues.length === 0) {
    return (
      <div className="rounded-[13px] border border-dashed border-border px-6 py-16 text-center">
        <p className="font-semibold">No safety issues found</p>
        <p className="mt-1 text-sm text-muted-foreground">Try adjusting the search or filters.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 md:hidden">
        {issues.map((issue) => {
          const href = layoutLink(issue, safetyPath);
          const content = (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 flex-1 font-semibold leading-5">{issue.description}</p>
                <IssueStatus issue={issue} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                <div>
                  <dt className="font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Layout
                  </dt>
                  <dd className="mt-1 font-medium">
                    {issue.layout == null ? '—' : (layoutNames.get(issue.layout) ?? '—')}
                  </dd>
                </div>
                <div>
                  <dt className="font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Location
                  </dt>
                  <dd className="mt-1 font-medium">{issue.location_detail?.name ?? '—'}</dd>
                </div>
                <div>
                  <dt className="font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Trade
                  </dt>
                  <dd className="mt-1 font-medium">{issue.trade_detail?.name ?? '—'}</dd>
                </div>
                <div>
                  <dt className="font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Reported
                  </dt>
                  <dd className="mt-1 font-medium">
                    {format(parseISO(issue.created_at), 'MMM d, yyyy')}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Reported by
                  </dt>
                  <dd className="mt-1 font-medium">{getSafetyIssueCreatorName(issue)}</dd>
                </div>
              </dl>
              {href && (
                <span className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-[10px] border border-border px-3 py-2 text-sm font-semibold text-muted-foreground">
                  <MapPinned className="size-4" />
                  View on layout
                </span>
              )}
            </>
          );

          return href ? (
            <Link
              key={issue.id}
              to={href}
              aria-label={`Open ${issue.description}`}
              className="block rounded-[13px] border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {content}
            </Link>
          ) : (
            <article key={issue.id} className="rounded-[13px] border border-border bg-card p-4">
              {content}
            </article>
          );
        })}
      </div>

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead
                label="Issue"
                sortKey="description"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                className="w-[110px]"
                label="Status"
                sortKey="status"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead label="Area" sortKey="layout" sort={sort} onSortChange={onSortChange} />
              <SortableHead
                label="Location"
                sortKey="location"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead label="Trade" sortKey="trade" sort={sort} onSortChange={onSortChange} />
              <SortableHead
                label="Reported by"
                sortKey="created_by"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                className="whitespace-nowrap"
                label="Reported"
                sortKey="created_at"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead className="w-[72px] text-xs font-medium">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.map((issue) => {
              const href = layoutLink(issue, safetyPath);
              const openIssue = () => {
                if (href) navigate(href);
              };
              return (
                <TableRow
                  key={issue.id}
                  role={href ? 'link' : undefined}
                  tabIndex={href ? 0 : undefined}
                  aria-label={href ? `Open ${issue.description}` : undefined}
                  onClick={openIssue}
                  onKeyDown={(event) => {
                    if (!href || (event.key !== 'Enter' && event.key !== ' ')) return;
                    event.preventDefault();
                    openIssue();
                  }}
                  className={href ? 'cursor-pointer hover:bg-muted/50' : undefined}
                >
                  <TableCell className="max-w-[360px] py-1.5 text-xs font-medium">
                    <span className="line-clamp-2">{issue.description}</span>
                  </TableCell>
                  <TableCell className="py-1.5">
                    <IssueStatus issue={issue} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-xs text-muted-foreground">
                    {issue.layout == null ? '—' : (layoutNames.get(issue.layout) ?? '—')}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs text-muted-foreground">
                    {issue.location_detail?.name ?? '—'}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs text-muted-foreground">
                    {issue.trade_detail?.name ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-xs text-muted-foreground">
                    {getSafetyIssueCreatorName(issue)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1.5 text-xs text-muted-foreground">
                    {format(parseISO(issue.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {href ? (
                      <MapPinned className="size-4 text-muted-foreground" aria-hidden="true" />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

const SortableHead = ({
  label,
  sortKey,
  sort,
  onSortChange,
  className,
}: {
  label: string;
  sortKey: SafetyIssuesSortKey;
  sort: readonly SafetyIssuesSortState[];
  onSortChange: (key: SafetyIssuesSortKey) => void;
  className?: string;
}) => {
  const sortIndex = sort.findIndex((rule) => rule.key === sortKey);
  const activeRule = sortIndex >= 0 ? sort[sortIndex] : null;
  const Icon = activeRule ? (activeRule.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <TableHead className={`text-xs font-medium ${className ?? ''}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onSortChange(sortKey)}
        className="-ml-2 h-8 gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
        title={`Sort by ${label}`}
      >
        {label}
        <Icon className="size-3.5" />
        {activeRule && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/15 px-1 text-[10px] font-bold text-primary">
            {sortIndex + 1}
          </span>
        )}
      </Button>
    </TableHead>
  );
};
